/**
 * Fun-ASR语音识别服务（模拟面试专用版本）
 * 基于阿里云DashScope Fun-ASR-Realtime的语音识别服务
 * 专门为模拟面试场景优化，提供低延迟的实时转录
 */

import browserCompatibility from '../utils/browserCompatibility.js';

class FunAsrInterviewService {
  constructor() {
    // WebSocket连接必须直接指向后端，不能通过前端代理
    // 使用相对路径，通过Nginx代理访问后端API
    this.baseURL = process.env.REACT_APP_API_BASE_URL || '';
    this.isRunning = false;
    this.isConnected = false;
    this.audioContext = null;
    this.audioStream = null;
    this.audioWorklet = null;
    this.recognitionResults = [];
    this.eventListeners = {};
    
    // WebSocket 相关属性
    this.ws = null;
    this.wsMode = false;
    this.wsReady = false;
    
    // WebSocket心跳和重连机制
    this.heartbeatInterval = null; // 心跳定时器
    this.reconnectAttempts = 0; // 重连尝试次数
    this.maxReconnectAttempts = 5; // 最大重连次数
    this.reconnectDelay = 3000; // 重连延迟（毫秒）
    this.isManualStop = false; // 是否手动停止（用于区分主动停止和异常断开）
    this.lastAudioDeviceId = null; // 上次使用的音频设备ID（用于重连）
    this.lastMaxSentenceSilence = null; // 上次的静音判句参数
    
    // AudioContext状态监控
    this.audioContextCheckInterval = null; // AudioContext状态检查定时器
    
    // 绑定方法
    this.onResult = this.onResult.bind(this);
    this.onError = this.onError.bind(this);
    this.onStart = this.onStart.bind(this);
    this.onEnd = this.onEnd.bind(this);
  }

  /**
   * 添加事件监听器
   */
  addEventListener(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }
    this.eventListeners[event].push(callback);
  }

  /**
   * 移除事件监听器
   */
  removeEventListener(event, callback) {
    if (this.eventListeners[event]) {
      this.eventListeners[event] = this.eventListeners[event].filter(cb => cb !== callback);
    }
  }

  /**
   * 触发事件
   */
  emit(event, data) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach(callback => callback(data));
    }
  }

  /**
   * 使用WebSocket方式启动识别（低延迟）
   */
  async startWebSocketRecognition(audioDeviceId = null, maxSentenceSilence = 3000) {
    try {
      // 清理可能的旧连接
      if (this.ws) {
        try { this.ws.close(); } catch (_) {}
        this.ws = null;
      }
      this.wsMode = true;
      this.wsReady = false;
      this.recognitionResults = [];

      // 获取token用于WebSocket认证
      const token = localStorage.getItem('access_token') || '';
      
      // 构造 ws/wss 地址 - 使用模拟面试专用的ASR端点
      // WebSocket 必须直接连接到后端，不能通过前端代理
      let wsBase;
      if (this.baseURL) {
        wsBase = this.baseURL.replace(/^http/, 'ws');
      } else {
        // 开发环境：使用后端地址（localhost:8000）
        // 生产环境：使用当前页面的协议和主机
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          // 开发环境，前端在3000端口，后端在8000端口
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          wsBase = `${protocol}//${window.location.hostname}:8000`;
        } else {
          // 生产环境，使用当前页面的协议和主机
          const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
          wsBase = `${protocol}//${window.location.host}`;
        }
      }
      // 将token作为query参数传递（WebSocket不支持自定义header）
      const wsUrl = token 
        ? `${wsBase}/api/v1/interview-asr?token=${encodeURIComponent(token)}`
        : `${wsBase}/api/v1/interview-asr`;
      
      // 使用兼容性工具创建WebSocket
      const ws = browserCompatibility.createWebSocket(wsUrl);
      this.ws = ws;

      return new Promise((resolve, reject) => {
        const timeoutId = setTimeout(() => {
          if (!this.wsReady) {
            try { ws.close(); } catch (_) {}
            reject(new Error('WebSocket connection timeout'));
          }
        }, 5000);

        ws.onopen = () => {
          // 初始化消息
          const initMessage = {
            action: 'init',
            language: 'zh-CN',
            max_sentence_silence: maxSentenceSilence
          };
          ws.send(JSON.stringify(initMessage));
        };

        ws.onmessage = async (evt) => {
          try {
            const data = JSON.parse(evt.data);
            if (data.type === 'ready') {
              clearTimeout(timeoutId);
              this.isRunning = true;
              this.isConnected = true;
              this.wsReady = true;
              
              // 开始采集音频
              try {
                await this.startAudioCapture(audioDeviceId);
                
                // 启动心跳机制
                this.startHeartbeat();
                
                // 启动AudioContext状态监控
                this.startAudioContextMonitor();
                
                // 重连成功，重置计数
                if (this.reconnectAttempts > 0) {
                  console.log('✅ 重连成功');
                  this.reconnectAttempts = 0;
                }
                
                this.emit('start', { message: 'Fun-ASR语音识别已启动(WS)' });
                resolve(true);
              } catch (err) {
                reject(err);
              }
              return;
            }
            
            if (data.type === 'interim' || data.type === 'final') {
              const isFinal = data.type === 'final';
              const nowTs = data.timestamp ? data.timestamp * 1000 : Date.now();
              
              const latest = {
                text: (data.text || '').trim(),
                timestamp: nowTs,
                is_sentence_end: isFinal,
                confidence: 0.85,
                source: 'paraformer'
              };
              
              // 更新本地缓存
              if (isFinal) {
                if (this.recognitionResults.length > 0 && !this.recognitionResults[this.recognitionResults.length - 1].is_sentence_end) {
                   this.recognitionResults[this.recognitionResults.length - 1] = latest;
                } else {
                   this.recognitionResults.push(latest);
                   // 防止内存泄漏，只保留最近50条结果
                   if (this.recognitionResults.length > 50) {
                     this.recognitionResults = this.recognitionResults.slice(-50);
                   }
                }
              } else {
                if (this.recognitionResults.length > 0 && !this.recognitionResults[this.recognitionResults.length - 1].is_sentence_end) {
                  this.recognitionResults[this.recognitionResults.length - 1] = latest;
                } else {
                  this.recognitionResults.push(latest);
                  // 防止内存泄漏，只保留最近50条结果
                  if (this.recognitionResults.length > 50) {
                    this.recognitionResults = this.recognitionResults.slice(-50);
                  }
                }
              }
              
              // 触发结果事件
              this.emit('result', {
                text: latest.text,
                isFinal: isFinal,
                confidence: latest.confidence,
                timestamp: nowTs,
                source: 'fun-asr',
                allResults: this.recognitionResults
              });

              if (isFinal) {
              }
            } else if (data.type === 'error') {
              this.emit('error', { error: data.message });
            }
          } catch (e) {
          }
        };

        ws.onclose = () => {
          this.wsReady = false;
          this.isConnected = false;
          this.isRunning = false;
          
          // 停止心跳
          this.stopHeartbeat();
          
          // 如果不是手动停止，尝试重连
          if (!this.isManualStop && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log(`🔄 WebSocket断开，尝试重连 (${this.reconnectAttempts}/${this.maxReconnectAttempts})...`);
            setTimeout(() => {
              this.startWebSocketRecognition(this.lastAudioDeviceId, this.lastMaxSentenceSilence)
                .catch(err => {
                  console.error('重连失败:', err);
                  if (this.reconnectAttempts >= this.maxReconnectAttempts) {
                    this.emit('error', { error: '连接断开，重连失败' });
                    this.emit('end', { message: 'Fun-ASR语音识别已停止(WS)' });
                  }
                });
            }, this.reconnectDelay);
          } else {
            this.emit('end', { message: 'Fun-ASR语音识别已停止(WS)' });
          }
        };

        ws.onerror = (err) => {
          if (!this.wsReady) {
             clearTimeout(timeoutId);
             reject(new Error('WebSocket connection error'));
          } else {
             this.emit('error', { error: 'WebSocket 连接失败' });
          }
        };
      });
    } catch (error) {
      this.wsMode = false;
      throw error;
    }
  }

  /**
   * 开始语音识别
   */
  async startRecognition(audioDeviceId = null, maxSentenceSilence = 5000) {
    try {
      this.isManualStop = false; // 重置手动停止标志
      this.lastAudioDeviceId = audioDeviceId; // 保存设备ID用于重连
      this.lastMaxSentenceSilence = maxSentenceSilence; // 保存参数用于重连
      this.reconnectAttempts = 0; // 重置重连计数
      
      // 使用WebSocket模式 (低延迟)
      // maxSentenceSilence默认5秒，给用户更多思考和表达时间，避免长句被截断
      const ok = await this.startWebSocketRecognition(audioDeviceId, maxSentenceSilence);
      if (!ok) {
        throw new Error('WebSocket启动失败');
      }
      return true;
    } catch (error) {
      this.stopAudioCapture();
      this.emit('error', { error: error.message });
      return false;
    }
  }

  /**
   * 停止语音识别
   */
  async stopRecognition() {
    try {
      // 标记为手动停止，阻止自动重连
      this.isManualStop = true;
      
      // 停止心跳
      this.stopHeartbeat();
      
      // 停止AudioContext监控
      this.stopAudioContextMonitor();
      
      // 停止音频捕获
      this.stopAudioCapture();
      
      if (this.ws) {
        try { this.ws.close(); } catch (_) {}
      }
      this.ws = null;
      this.wsMode = false;
      this.wsReady = false;
      
      this.isRunning = false;
      this.isConnected = false;
      this.emit('end', { message: 'Fun-ASR语音识别已停止' });
      
      return true;
    } catch (error) {
      this.emit('error', { error: error.message });
      return false;
    }
  }

  /**
   * 开始音频捕获
   */
  async startAudioCapture(audioDeviceId = null) {
    try {
      // 构建音频约束条件
      const audioConstraints = {
        sampleRate: 16000,
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      
      // 如果指定了设备ID，添加设备约束
      if (audioDeviceId && audioDeviceId !== 'default' && audioDeviceId !== '') {
        audioConstraints.deviceId = { exact: audioDeviceId };
        // 对于电脑音频设备，通常需要关闭音频处理
        audioConstraints.echoCancellation = false;
        audioConstraints.noiseSuppression = false;
        audioConstraints.autoGainControl = false;
      } else {
      }
      
      // 获取音频流（使用兼容性工具）
      this.audioStream = await browserCompatibility.getUserMedia({
        audio: audioConstraints
      });

      // 创建音频上下文（使用兼容性工具）
      this.audioContext = browserCompatibility.createAudioContext({
        sampleRate: 16000
      });
      
      // 恢复音频上下文（处理浏览器自动挂起）
      await browserCompatibility.resumeAudioContext(this.audioContext);

      // 创建音频源
      const source = this.audioContext.createMediaStreamSource(this.audioStream);
      
      // 使用兼容性工具创建音频处理器（模拟面试专用版本，无增强处理）
      try {
        this.audioProcessor = await browserCompatibility.createAudioProcessor(this.audioContext, {
          workletUrl: '/audio-processor-interview.js',
          workletName: 'audio-processor',
          bufferSize: 4096,
          inputChannels: 1,
          outputChannels: 1,
          onAudioData: (audioData) => {
            if (this.isRunning && this.isConnected) {
              const pcmData = this.floatTo16BitPCM(audioData);
              this.sendAudioData(pcmData);
            }
          }
        });
        
        // 连接音频节点
        source.connect(this.audioProcessor.node);
        this.audioProcessor.node.connect(this.audioContext.destination);
      } catch (error) {
        console.error('创建音频处理器失败:', error);
        throw error;
      }
    } catch (error) {
      throw error;
    }
  }

  /**
   * 设置ScriptProcessor（回退方案）
   */
  setupScriptProcessor(source) {
    // 音频缓冲区 - 减小缓冲区大小，提高响应速度
    this.audioBuffer = [];
    this.bufferSize = 0;
    this.maxBufferSize = 4800; // 300ms的音频数据 (16000 * 0.3)
    
    // 创建音频处理器 - 使用较小的缓冲区
    const processor = this.audioContext.createScriptProcessor(2048, 1, 1);
    
    processor.onaudioprocess = (event) => {
      if (this.isRunning && this.isConnected) {
        const inputBuffer = event.inputBuffer;
        const inputData = inputBuffer.getChannelData(0);
        
        // 检查音频数据是否有效（不是静音）
        const hasAudio = this.hasValidAudio(inputData);
        
        if (hasAudio) {
          // 有有效音频时，将数据添加到缓冲区
          this.audioBuffer.push(new Float32Array(inputData));
          this.bufferSize += inputData.length;
          
          // 当缓冲区达到一定大小时发送数据
          if (this.bufferSize >= this.maxBufferSize) {
            this.flushAudioBuffer();
          }
        } else {
          // 静音时，减少发送频率，只在必要时发送心跳数据
          const now = Date.now();
          if (!this.lastSilenceTime || (now - this.lastSilenceTime) > 2000) { // 每2秒发送一次静音心跳
            this.sendSilenceHeartbeat();
            this.lastSilenceTime = now;
          }
        }
      }
    };
    
    source.connect(processor);
    processor.connect(this.audioContext.destination);
    
    this.processor = processor;
  }
  
  /**
   * 检查音频数据是否包含有效音频（非静音）
   */
  hasValidAudio(audioData) {
    const threshold = 0.005; // 降低音频阈值，提高敏感度
    let maxAmplitude = 0;
    
    // 计算最大振幅
    for (let i = 0; i < audioData.length; i++) {
      const amplitude = Math.abs(audioData[i]);
      if (amplitude > maxAmplitude) {
        maxAmplitude = amplitude;
      }
    }
    
    // 如果最大振幅超过阈值，认为有有效音频
    return maxAmplitude > threshold;
  }

  /**
   * 刷新音频缓冲区
   */
  flushAudioBuffer() {
    if (this.audioBuffer.length === 0) return;
    
    // 合并所有缓冲的音频数据
    const totalLength = this.audioBuffer.reduce((sum, buffer) => sum + buffer.length, 0);
    const combinedBuffer = new Float32Array(totalLength);
    
    let offset = 0;
    for (const buffer of this.audioBuffer) {
      combinedBuffer.set(buffer, offset);
      offset += buffer.length;
    }
    
    // 转换为PCM数据并发送
    const pcmData = this.floatTo16BitPCM(combinedBuffer);
    this.sendAudioData(pcmData);
    
    // 清空缓冲区
    this.audioBuffer = [];
    this.bufferSize = 0;
  }

  /**
   * 发送静音心跳数据（保持长连接）
   */
  sendSilenceHeartbeat() {
    try {
      // 生成静音音频数据（100ms的静音）
      const silenceDuration = 0.1; // 100ms
      const sampleCount = Math.floor(16000 * silenceDuration); // 1600个样本
      const silenceData = new Float32Array(sampleCount); // 全零数组，表示静音
      
      const pcmData = this.floatTo16BitPCM(silenceData);
      
      this.sendAudioData(pcmData);
    } catch (error) {
    }
  }

  /**
   * 停止音频捕获
   */
  stopAudioCapture() {
    try {
      // 刷新剩余的音频缓冲区
      if (this.audioBuffer && this.audioBuffer.length > 0) {
        this.flushAudioBuffer();
      }
      
      // 断开音频处理器（使用兼容性工具）
      if (this.audioProcessor) {
        this.audioProcessor.disconnect();
        this.audioProcessor = null;
      }
      
      // 旧的audioWorklet引用（向后兼容）
      if (this.audioWorklet) {
        try {
          this.audioWorklet.disconnect();
        } catch (e) {
          console.warn('断开audioWorklet失败:', e);
        }
        this.audioWorklet = null;
      }
      
      if (this.processor) {
        this.processor.disconnect();
        this.processor = null;
      }
      
      if (this.audioStream) {
        this.audioStream.getTracks().forEach(track => track.stop());
        this.audioStream = null;
      }
      
      if (this.audioContext && this.audioContext.state !== 'closed') {
        this.audioContext.close();
        this.audioContext = null;
      }
      
      this.audioBuffer = [];
      this.bufferSize = 0;
    } catch (error) {
      console.warn('停止音频捕获时出错:', error);
    }
  }
  
  /**
   * 启动WebSocket心跳机制
   * 每30秒发送一次静音数据，保持连接活跃
   */
  startHeartbeat() {
    // 清除旧的心跳定时器
    this.stopHeartbeat();
    
    // 每30秒发送一次心跳（静音数据）
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        try {
          // 发送一个很小的静音数据作为心跳
          const silenceDuration = 0.05; // 50ms
          const sampleCount = Math.floor(16000 * silenceDuration);
          const silenceData = new Float32Array(sampleCount);
          const pcmData = this.floatTo16BitPCM(silenceData);
          this.ws.send(pcmData);
        } catch (error) {
          console.warn('心跳发送失败:', error);
        }
      }
    }, 30000); // 30秒
  }
  
  /**
   * 停止WebSocket心跳
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }
  
  /**
   * 启动AudioContext状态监控
   * 防止AudioContext被浏览器挂起
   */
  startAudioContextMonitor() {
    // 清除旧的监控定时器
    this.stopAudioContextMonitor();
    
    // 每10秒检查一次AudioContext状态
    this.audioContextCheckInterval = setInterval(() => {
      if (this.audioContext) {
        if (this.audioContext.state === 'suspended') {
          console.warn('⚠️ AudioContext已挂起，尝试恢复...');
          this.audioContext.resume().catch(err => {
            console.error('恢复AudioContext失败:', err);
          });
        }
      }
    }, 10000); // 10秒
  }
  
  /**
   * 停止AudioContext状态监控
   */
  stopAudioContextMonitor() {
    if (this.audioContextCheckInterval) {
      clearInterval(this.audioContextCheckInterval);
      this.audioContextCheckInterval = null;
    }
  }

  /**
   * 发送音频数据到后端
   */
  async sendAudioData(pcmData) {
    try {
      if (!pcmData || pcmData.byteLength === 0) {
        return;
      }
      
      if (this.wsMode && this.ws && this.wsReady && this.ws.readyState === WebSocket.OPEN) {
        try {
          this.ws.send(pcmData);
          return;
        } catch (wsError) {
          return;
        }
      }
      
    } catch (error) {
    }
  }

  // WebSocket模式无需轮询，结果通过WebSocket实时接收

  /**
   * 获取识别结果
   */
  getResults() {
    return this.recognitionResults;
  }

  /**
   * 清空识别结果
   */
  async clearResults() {
    this.recognitionResults = [];
  }

  /**
   * 获取服务状态
   */
  async getStatus() {
    return {
      is_running: this.isRunning,
      is_connected: this.isConnected,
      results_count: this.recognitionResults.length
    };
  }

  /**
   * 将Float32Array转换为16位PCM数据
   */
  floatTo16BitPCM(input) {
    // 确保输入数据有效
    if (!input || input.length === 0) {
      return new ArrayBuffer(0);
    }
    
    const buffer = new ArrayBuffer(input.length * 2);
    const view = new DataView(buffer);
    let offset = 0;
    
    for (let i = 0; i < input.length; i++, offset += 2) {
      // 限制范围到[-1, 1]
      const s = Math.max(-1, Math.min(1, input[i]));
      // 转换为16位整数，使用小端序
      const sample = s < 0 ? s * 0x8000 : s * 0x7FFF;
      view.setInt16(offset, sample, true);
    }
    
    return buffer;
  }

  /**
   * 处理识别结果
   */
  onResult(data) {
    this.emit('result', data);
  }

  /**
   * 处理错误
   */
  onError(error) {
    this.emit('error', error);
  }

  /**
   * 处理开始事件
   */
  onStart(data) {
    this.emit('start', data);
  }

  /**
   * 处理结束事件
   */
  onEnd(data) {
    this.emit('end', data);
  }

  /**
   * 销毁服务
   */
  destroy() {
    this.isManualStop = true; // 标记为手动销毁
    this.stopHeartbeat();
    this.stopAudioContextMonitor();
    this.stopRecognition();
    this.eventListeners = {};
  }
}

export default FunAsrInterviewService;

